import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import { formatPriceForMapPin, formatPriceRangeForMapPin } from '../lib/utils';

interface PropertyMapProps {
  latitude: number;
  longitude: number;
  address?: string;
  className?: string;
  interactiveOnTap?: boolean; // when true, disable interactions until user taps
  markers?: Array<{
    lat: number;
    lng: number;
    id?: string | number;
    label?: string;
    price?: number;
    priceMin?: number;
    priceMax?: number;
  }>;
  onMarkerClick?: (marker: { lat: number; lng: number; id?: string | number; label?: string }) => void;
  onCenterChange?: (center: { lat: number; lng: number }) => void;
  onZoomChange?: (zoom: number) => void;
  fitBoundsPoints?: Array<{ lat: number; lng: number }>;
  fitBoundsTrigger?: number;
  forceFitBounds?: boolean;
  initialZoom?: number;
  hasSavedView?: boolean;
  suppressInitialAutoFit?: boolean;
  restoreZoomOnMount?: number | undefined;
}

// Fix for default markers in Leaflet with Vite
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

export default function PropertyMap({ latitude, longitude, address, className = '', interactiveOnTap = true, markers = [], onMarkerClick, onCenterChange, onZoomChange, fitBoundsPoints = [], fitBoundsTrigger, forceFitBounds = false, initialZoom, hasSavedView = false, suppressInitialAutoFit = false, restoreZoomOnMount, priceUnavailableLabel }: PropertyMapProps & { priceUnavailableLabel?: string }) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const isInteractiveRef = useRef<boolean>(!interactiveOnTap);
  const markersLayerRef = useRef<L.LayerGroup | null>(null);
  const userHasInteractedRef = useRef<boolean>(false);
  const isProgrammaticMoveRef = useRef<boolean>(false);
  const lastFitSignatureRef = useRef<string | null>(null);
  const suppressInitialAutoFitRef = useRef<boolean>(suppressInitialAutoFit);
  const suppressInitialZoomEventRef = useRef<boolean>(true);
  const [currentZoom, setCurrentZoom] = useState<number>(initialZoom || 9);
  const markerRefs = useRef<L.Marker[]>([]);
  const userLocationMarkerRef = useRef<L.Marker | null>(null);
  

  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    // Initialize the map with default center, will be updated by panTo
    const map = L.map(mapRef.current, {
      zoomControl: false,
    }).setView([latitude, longitude], initialZoom || 9);

    // Add OpenStreetMap tiles
    const tiles = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19,
    }).addTo(map);
    try {
      // Raise tile pane so our external UI overlays can layer above controls
      (map.getPanes().tilePane as any).style.zIndex = '200';
      (map.getPanes().overlayPane as any).style.zIndex = '250';
      (map.getPanes().markerPane as any).style.zIndex = '300';
      (map.getPanes().popupPane as any).style.zIndex = '400';
    } catch {}

    // Center marker removed - using interactive property pins instead

    // Create a layer group for property markers
    markersLayerRef.current = L.layerGroup().addTo(map);

    // Store map instance
    mapInstanceRef.current = map;
    // If an explicit zoom restore is provided (return from details), enforce it once
    if (typeof restoreZoomOnMount === 'number') {
      try {
        isProgrammaticMoveRef.current = true;
        map.setZoom(restoreZoomOnMount);
        map.once('zoomend', () => {
          isProgrammaticMoveRef.current = false;
        });
      } catch {}
    }
    // Mark when user interacts (so we don't auto-reset zoom later)
    const handleUserInteract = () => {
      if (isProgrammaticMoveRef.current) return;
      userHasInteractedRef.current = true;
    };
    map.on('zoomstart', handleUserInteract);
    map.on('movestart', handleUserInteract);

    // Disable interactions until user taps (if enabled)
    const disableAllInteractions = () => {
      map.scrollWheelZoom.disable();
      map.dragging.disable();
      map.doubleClickZoom.disable();
      map.boxZoom.disable();
      map.keyboard.disable();
      (map as any).touchZoom && map.touchZoom.disable();
    };
    const enableAllInteractions = () => {
      map.scrollWheelZoom.enable();
      map.dragging.enable();
      map.doubleClickZoom.enable();
      map.boxZoom.enable();
      map.keyboard.enable();
      (map as any).touchZoom && map.touchZoom.enable();
    };
    if (interactiveOnTap) {
      disableAllInteractions();
      isInteractiveRef.current = false;
      const handleFirstPointer = () => {
        if (!isInteractiveRef.current) {
          enableAllInteractions();
          isInteractiveRef.current = true;
          map.getContainer().classList.remove('leaflet-touch-blocked');
          map.getContainer().removeEventListener('pointerdown', handleFirstPointer);
        }
      };
      map.getContainer().classList.add('leaflet-touch-blocked');
      map.getContainer().addEventListener('pointerdown', handleFirstPointer);
    } else {
      // leave interactions enabled
      isInteractiveRef.current = true;
    }

    // Cleanup function
    return () => {
      // Clean up markers
      markerRefs.current.forEach(marker => {
        try {
          if (markersLayerRef.current) {
            markersLayerRef.current.removeLayer(marker);
          }
        } catch (error) {
        }
      });
      markerRefs.current = [];
      
      if (mapInstanceRef.current) {
        mapInstanceRef.current.off('zoomstart', handleUserInteract);
        mapInstanceRef.current.off('movestart', handleUserInteract);
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
      
    };
  }, []); // Only initialize once

  // React to external center changes without resetting zoom
  useEffect(() => {
    if (!mapInstanceRef.current) return;
    const map = mapInstanceRef.current;
    const current = map.getCenter();
    const deltaLat = Math.abs(current.lat - latitude);
    const deltaLng = Math.abs(current.lng - longitude);
    const threshold = 0.00001;
    if (deltaLat > threshold || deltaLng > threshold) {
      try {
        const before = map.getCenter();
        const beforeZoom = map.getZoom();
        const nextZoom = Math.max(beforeZoom || 18, 18);
        map.setView([latitude, longitude], nextZoom, { animate: true });
      } catch {}
    }
  }, [latitude, longitude]);

  // Center marker removed - no longer needed

  // Update markers with price indicators when zoom level changes
  useEffect(() => {
    if (!mapInstanceRef.current || !markersLayerRef.current) return;
    const layer = markersLayerRef.current;
    
    // Remove existing markers safely
    markerRefs.current.forEach(marker => {
      try {
        layer.removeLayer(marker);
      } catch (error) {
      }
    });
    markerRefs.current = [];
    
    // Create new markers
    markers.forEach((m) => {
      // Create custom icon with price indicator if zoom level is 12 or higher and price is available
      let icon;
      if (currentZoom >= 12 && (m.price || m.priceMin || m.priceMax || priceUnavailableLabel)) {
        const priceText = (m.priceMin || m.priceMax)
          ? formatPriceRangeForMapPin(m.priceMin, m.priceMax)
          : (m.price ? formatPriceForMapPin(m.price as number) : (priceUnavailableLabel || ''));
        if (priceText) {
          // Create a custom div icon with price indicator
          const priceIcon = L.divIcon({
            className: 'custom-price-marker',
            html: `
              <div style="
                background: #2563eb;
                color: white;
                border: 2px solid white;
                border-radius: 20px;
                padding: 4px 8px;
                font-size: 12px;
                font-weight: bold;
                text-align: center;
                box-shadow: 0 2px 4px rgba(0,0,0,0.3);
                white-space: nowrap;
                min-width: 50px;
              ">
                ${priceText}
              </div>
            `,
            iconSize: [Math.max(60, Math.min(120, priceText.length * 9)), 20],
            iconAnchor: [30, 10]
          });
          icon = priceIcon;
        }
      }
      
      try {
        const mk = icon
          ? L.marker([m.lat, m.lng], { icon })
          : L.marker([m.lat, m.lng]);
        if (m.label) {
          mk.bindPopup(m.label);
        }
        if (onMarkerClick) {
          mk.on('click', () => onMarkerClick(m));
        }
        mk.addTo(layer);
        markerRefs.current.push(mk);
      } catch (error) {
      }
    });
  }, [markers, currentZoom]);

  // Notify center changes to parent
  useEffect(() => {
    if (!mapInstanceRef.current || !onCenterChange) return;
    const map = mapInstanceRef.current;
    const handler = () => {
      const c = map.getCenter();
      onCenterChange({ lat: c.lat, lng: c.lng });
    };
    map.on('moveend', handler);
    map.on('zoomend', () => {
      // Ignore the very first Leaflet zoomend and any programmatic zooms
      if (suppressInitialZoomEventRef.current) {
        suppressInitialZoomEventRef.current = false;
        return;
      }
      if (isProgrammaticMoveRef.current) {
        return;
      }
      const zoom = map.getZoom();
      setCurrentZoom(zoom);
      if (onZoomChange) {
        onZoomChange(zoom);
      }
    });
    return () => {
      map.off('moveend', handler);
    };
  }, [onCenterChange]);

  // Fit bounds on trigger (always fit on new trigger), or force if requested
  useEffect(() => {
    if (!mapInstanceRef.current) return;
    if (!fitBoundsPoints || fitBoundsPoints.length === 0) return;
    try {

      // One-time suppression to preserve prior view when returning to map
      if (suppressInitialAutoFitRef.current) {
        suppressInitialAutoFitRef.current = false;
        return;
      }

      // Avoid re-fitting to identical points unless forced
      const signature = (() => {
        const n = fitBoundsPoints.length;
        if (n === 0) return '0';
        const first = fitBoundsPoints[0];
        const last = fitBoundsPoints[n - 1];
        return `${n}:${first.lat.toFixed(5)},${first.lng.toFixed(5)}|${last.lat.toFixed(5)},${last.lng.toFixed(5)}`;
      })();
      if (!forceFitBounds && lastFitSignatureRef.current === signature) {
        return;
      }
      lastFitSignatureRef.current = signature;
      // If forced, always fit
      if (forceFitBounds && fitBoundsPoints.length > 1) {
        const bounds = L.latLngBounds(fitBoundsPoints.map(p => L.latLng(p.lat, p.lng)));
        isProgrammaticMoveRef.current = true;
        mapInstanceRef.current.fitBounds(bounds, { padding: [24, 24] });
        // Reset programmatic flag after the move completes
        mapInstanceRef.current.once('moveend', () => {
          isProgrammaticMoveRef.current = false;
        });
      } else {
        // Only auto-fit when there are multiple points; otherwise keep current view
        if (fitBoundsPoints.length > 1) {
          const bounds = L.latLngBounds(fitBoundsPoints.map(p => L.latLng(p.lat, p.lng)));
          isProgrammaticMoveRef.current = true;
          mapInstanceRef.current.fitBounds(bounds, { padding: [24, 24] });
          mapInstanceRef.current.once('moveend', () => {
            isProgrammaticMoveRef.current = false;
          });
        } else {
        }
      }
      // Reset user interaction flag for the next cycle
      userHasInteractedRef.current = false;
    } catch (err) {
    }
  }, [fitBoundsTrigger, forceFitBounds, hasSavedView]);

  return (
    <div className={`w-full relative`}>
      <div 
        ref={mapRef} 
        className={`w-full rounded-lg border border-gray-200 h-64 ${className}`}
      />
      {(!isInteractiveRef.current) && (
        <div
          className="absolute inset-0 flex items-center justify-center"
          style={{ pointerEvents: 'none' }}
        >
          <div className="pointer-events-auto select-none px-4 py-2 rounded-full bg-white/90 border border-blue-200 shadow text-sm font-semibold text-blue-700">
            Tap to interact
          </div>
        </div>
      )}
    </div>
  );
} 